import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  cloneMetricFilters,
  completeMetricFilters,
  createEmptyMetricFilter,
  loadSavedMetricFilters,
  saveSavedMetricFilters,
  serializeMetricFilters,
  toMetricFilterPayloads,
  type MetricFilter,
  type MetricFilterOperator,
  type MetricFilterOption,
  type MetricFilterPayload,
  type SavedMetricFilterPreset,
} from '@/utils/workspaceMetricFilters';

export function useMetricFilters({
  tableId,
  metrics,
}: {
  tableId: string;
  metrics: readonly MetricFilterOption[];
}) {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const defaultMetric = metrics[0]?.key ?? 'impressions';

  const [filters, setFilters] = useState<MetricFilter[]>([]);
  const [savedPresets, setSavedPresets] = useState<SavedMetricFilterPreset[]>([]);

  useEffect(() => {
    setFilters([]);
    setSavedPresets(loadSavedMetricFilters(profileId, tableId));
  }, [profileId, tableId]);

  const persistPresets = useCallback(
    (next: SavedMetricFilterPreset[]) => {
      setSavedPresets(next);
      saveSavedMetricFilters(profileId, tableId, next);
    },
    [profileId, tableId]
  );

  const addFilter = useCallback(() => {
    setFilters((prev) => [...prev, createEmptyMetricFilter(defaultMetric)]);
  }, [defaultMetric]);

  const updateFilter = useCallback(
    (
      id: string,
      patch: Partial<Pick<MetricFilter, 'metric' | 'operator' | 'value'>>
    ) => {
      setFilters((prev) =>
        prev.map((filter) => (filter.id === id ? { ...filter, ...patch } : filter))
      );
    },
    []
  );

  const removeFilter = useCallback((id: string) => {
    setFilters((prev) => prev.filter((filter) => filter.id !== id));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  const savePreset = useCallback(
    (name: string) => {
      const complete = completeMetricFilters(filters);
      if (complete.length === 0) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      persistPresets([
        ...savedPresets,
        {
          id: crypto.randomUUID(),
          name: trimmed,
          filters: cloneMetricFilters(complete),
        },
      ]);
    },
    [filters, persistPresets, savedPresets]
  );

  const applyPreset = useCallback((preset: SavedMetricFilterPreset) => {
    setFilters(cloneMetricFilters(preset.filters));
  }, []);

  const removePreset = useCallback(
    (id: string) => {
      persistPresets(savedPresets.filter((preset) => preset.id !== id));
    },
    [persistPresets, savedPresets]
  );

  const completeFilters = useMemo(() => completeMetricFilters(filters), [filters]);
  const payload = useMemo(() => toMetricFilterPayloads(filters), [filters]);
  const serialized = useMemo(() => serializeMetricFilters(filters), [filters]);

  return {
    filters,
    completeFilters,
    payload,
    serialized,
    savedPresets,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    savePreset,
    applyPreset,
    removePreset,
    setOperator: (id: string, operator: MetricFilterOperator) => updateFilter(id, { operator }),
  };
}

export type MetricFiltersController = ReturnType<typeof useMetricFilters> & {
  payload: MetricFilterPayload[];
};
